import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Sparkles, ExternalLink, Camera, Video, Calendar, Share2 } from "lucide-react";

// Extract a YouTube video id from any common YouTube URL form (youtu.be/, watch?v=, /embed/, /shorts/).
function ytId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") return u.pathname.slice(1).split("/")[0] || null;
    if (u.hostname.endsWith("youtube.com")) {
      if (u.searchParams.get("v")) return u.searchParams.get("v");
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.findIndex((p) => p === "embed" || p === "shorts");
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
    }
    return null;
  } catch {
    return null;
  }
}

// Pretty-print date range like "Mar 2026" if both start and end fall in the same month, otherwise "Mar–Apr 2026".
function prettyRange(start, end) {
  const M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const parse = (s) => { if (!s || s.length < 7) return null; const [y, m] = s.split("-").map(Number); return { y, m: m - 1 }; };
  const a = parse(start), b = parse(end);
  if (!a && !b) return "";
  if (a && !b) return `${M[a.m]} ${a.y}`;
  if (!a && b) return `${M[b.m]} ${b.y}`;
  if (a.y === b.y && a.m === b.m) return `${M[a.m]} ${a.y}`;
  if (a.y === b.y) return `${M[a.m]}–${M[b.m]} ${a.y}`;
  return `${M[a.m]} ${a.y} – ${M[b.m]} ${b.y}`;
}

function buildShareText(row) {
  const when = prettyRange(row.start_date, row.end_date);
  const prog = row.programme || "India Experience Program";
  const lines = [
    `Meet ${row.customer} — a participant on our ${prog}${when ? ` in ${when}` : ""}. 🌏`,
    ``,
    `📸 Photos: ${row.pictures_link}`,
    `🎥 Testimonial: ${row.testimonials_link}`,
    ``,
    `— Aii Group · Meaningful Adventures`,
  ];
  return lines.join("\n");
}

async function shareStory(row) {
  const text = buildShareText(row);
  const title = `Aii Group — ${row.customer}'s story`;
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url: row.testimonials_link });
      return;
    } catch (e) {
      if (e && e.name === "AbortError") return; // user cancelled
      // fall through to clipboard
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Story copied — paste into WhatsApp or LinkedIn");
  } catch {
    toast.error("Could not copy. Please try again.");
  }
}

function StoryCard({ row }) {
  const vid = ytId(row.testimonials_link);
  const thumb = vid ? `https://img.youtube.com/vi/${vid}/hqdefault.jpg` : null;

  return (
    <article data-testid={`story-${row.id}`} className="aii-card overflow-hidden flex flex-col group">
      <a href={row.testimonials_link} target="_blank" rel="noreferrer"
        className="relative aspect-video bg-[color:var(--aii-green-tint)] overflow-hidden block">
        {thumb ? (
          <img src={thumb} alt={`${row.customer} testimonial`}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Video size={48} className="text-brand/60" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent pointer-events-none" />
        <span className="absolute bottom-3 left-3 text-white text-xs font-semibold uppercase tracking-widest flex items-center gap-1.5">
          <Video size={12} /> Watch testimonial
        </span>
      </a>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-display text-lg font-semibold leading-tight">{row.customer}</h3>
        <div className="text-sm text-[color:var(--aii-text-2)] mt-0.5">{row.programme || "India Experience Program"}</div>
        {(row.start_date || row.end_date) && (
          <div className="text-xs text-[color:var(--aii-muted)] mt-2 flex items-center gap-1.5">
            <Calendar size={11} />
            {row.start_date || row.end_date ? `${row.start_date || "?"} → ${row.end_date || "?"}` : ""}
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-[color:var(--aii-border)] flex flex-wrap gap-2">
          <a data-testid={`story-pics-${row.id}`} href={row.pictures_link} target="_blank" rel="noreferrer"
            className="flex-1 min-w-[120px] text-center text-xs font-semibold px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark flex items-center justify-center gap-1.5">
            <Camera size={12} /> View pictures
          </a>
          <a data-testid={`story-video-${row.id}`} href={row.testimonials_link} target="_blank" rel="noreferrer"
            className="flex-1 min-w-[120px] text-center text-xs font-semibold px-3 py-2 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center justify-center gap-1.5">
            <ExternalLink size={12} /> Watch video
          </a>
          <button data-testid={`story-share-${row.id}`} onClick={() => shareStory(row)} title="Share this story"
            className="text-xs font-semibold px-3 py-2 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center justify-center gap-1.5">
            <Share2 size={12} /> Share
          </button>
        </div>
      </div>
    </article>
  );
}

export default function SuccessStories() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/program-bookings").then(({ data }) => {
      if (mounted) { setRows(data); setLoading(false); }
    }).catch(() => setLoading(false));
    return () => { mounted = false; };
  }, []);

  const stories = useMemo(
    () => rows.filter((r) => (r.pictures_link || "").trim() && (r.testimonials_link || "").trim()),
    [rows],
  );

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Social Proof</div>
        <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
          <Sparkles className="text-brand" /> Success Stories
        </h1>
        <p className="text-sm text-[color:var(--aii-text-2)] mt-1 max-w-2xl">
          Auto-generated from every participant who has both photos <em>and</em> a video testimonial in the
          Pictures &amp; Videos log. Great for partner meetings, decks and social media reposts.
        </p>
      </div>

      {loading ? (
        <div className="aii-card p-10 text-center text-[color:var(--aii-muted)]">Loading…</div>
      ) : stories.length === 0 ? (
        <div className="aii-card p-10 text-center" data-testid="story-empty">
          <Sparkles className="mx-auto mb-3 text-brand" size={28} />
          <div className="font-semibold">No stories yet</div>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1 max-w-md mx-auto">
            Stories appear here automatically once a participant has <b>both</b> a pictures link and a video
            testimonial saved in the <b>Pictures &amp; Videos</b> page.
          </p>
        </div>
      ) : (
        <>
          <div className="text-sm text-[color:var(--aii-text-2)]" data-testid="story-count">
            <b className="text-brand">{stories.length}</b> {stories.length === 1 ? "story" : "stories"} ready to share.
          </div>
          <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {stories.map((r) => <StoryCard key={r.id} row={r} />)}
          </div>
        </>
      )}
    </div>
  );
}
